/**
 * Human-readable labels for physical codes (rebinding UI, tutorial prompts).
 */
import { padIndexOf } from './types';

const KEY_LABELS: Record<string, string> = {
  ArrowLeft: '←',
  ArrowRight: '→',
  ArrowUp: '↑',
  ArrowDown: '↓',
  Space: 'SPACE',
  Enter: 'ENTER',
  NumpadEnter: 'NUM ENTER',
  Escape: 'ESC',
  Backspace: 'BKSP',
  Tab: 'TAB',
  ShiftLeft: 'L-SHIFT',
  ShiftRight: 'R-SHIFT',
  ControlLeft: 'L-CTRL',
  ControlRight: 'R-CTRL',
  AltLeft: 'L-ALT',
  AltRight: 'R-ALT',
  MetaLeft: 'L-CMD',
  MetaRight: 'R-CMD',
  Period: '.',
  Comma: ',',
  Slash: '/',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  BracketLeft: '[',
  BracketRight: ']',
  Minus: '-',
  Equal: '=',
  Backquote: '`',
  CapsLock: 'CAPS',
  NumpadAdd: 'NUM +',
  NumpadSubtract: 'NUM -',
  NumpadMultiply: 'NUM *',
  NumpadDivide: 'NUM /',
  NumpadDecimal: 'NUM .',
};

export const keyLabel = (code: string): string => {
  const fixed = KEY_LABELS[code];
  if (fixed) return fixed;
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `NUM ${code.slice(6)}`;
  if (/^F\d{1,2}$/.test(code)) return code;
  return code.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase();
};

export type PadFamily = 'xbox' | 'playstation' | 'nintendo' | 'generic';

/** Guess the controller family from Gamepad.id (labels only; the mapping is always the standard one). */
export const padFamily = (gamepadId: string): PadFamily => {
  const id = gamepadId.toLowerCase();
  if (/054c|dualsense|dualshock|playstation|ps[345]/.test(id)) return 'playstation';
  if (/057e|nintendo|pro controller|joy-con/.test(id)) return 'nintendo';
  if (/xbox|xinput|045e|microsoft/.test(id)) return 'xbox';
  return 'generic';
};

const XBOX = [
  'A',
  'B',
  'X',
  'Y',
  'LB',
  'RB',
  'LT',
  'RT',
  'VIEW',
  'MENU',
  'LS',
  'RS',
  'D↑',
  'D↓',
  'D←',
  'D→',
  'HOME',
];
const PS = [
  '✕',
  '○',
  '□',
  '△',
  'L1',
  'R1',
  'L2',
  'R2',
  'SHARE',
  'OPTIONS',
  'L3',
  'R3',
  'D↑',
  'D↓',
  'D←',
  'D→',
  'PS',
];
// Nintendo's standard mapping keeps positions (index 0 = bottom = B on the pad).
const NINTENDO = [
  'B',
  'A',
  'Y',
  'X',
  'L',
  'R',
  'ZL',
  'ZR',
  '−',
  '+',
  'LS',
  'RS',
  'D↑',
  'D↓',
  'D←',
  'D→',
  'HOME',
];

const AXIS_NAMES = [
  'L-STICK ←',
  'L-STICK →',
  'L-STICK ↑',
  'L-STICK ↓',
  'R-STICK ←',
  'R-STICK →',
  'R-STICK ↑',
  'R-STICK ↓',
];

export const padLabel = (code: string, family: PadFamily = 'generic'): string => {
  const axis = /^axis(\d)([-+])$/.exec(code);
  if (axis) {
    const i = Number(axis[1]);
    const neg = axis[2] === '-';
    const k = i * 2 + (neg ? 0 : 1);
    return i < 4 ? AXIS_NAMES[k]! : `AXIS ${i}${neg ? '−' : '+'}`;
  }
  const n = Number(code);
  if (!Number.isInteger(n)) return code;
  const table = family === 'playstation' ? PS : family === 'nintendo' ? NINTENDO : XBOX;
  return table[n] ?? `BTN ${n}`;
};

const TOUCH_LABELS: Record<string, string> = {
  left: '◀',
  right: '▶',
  jump: 'JUMP',
  down: '▼',
  action: 'ACTION',
  special: 'SPECIAL',
};

export const codeLabel = (deviceId: string, code: string, family: PadFamily = 'generic'): string => {
  if (deviceId === 'touch') return TOUCH_LABELS[code] ?? code.toUpperCase();
  if (padIndexOf(deviceId) >= 0) return padLabel(code, family);
  return keyLabel(code);
};
